import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Save, ArrowLeft, Upload, Trash2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function AlbumForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = id !== 'new';

    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [album, setAlbum] = useState({
        title: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        event_id: ''
    });
    const [photos, setPhotos] = useState([]);
    const [events, setEvents] = useState([]);

    useEffect(() => {
        // Rediriger si on essaie d'accéder à un album local (géré par le code)
        const isLocal = !isEditing ? false : ['concert-noel-2025', 'metz-2025', 'gand-2023', 'treves-2019'].includes(id);
        if (isLocal) {
            navigate('/admin/gallery');
            return;
        }

        fetchEvents();
        if (isEditing) {
            fetchAlbum();
            fetchPhotos();
        }
    }, [id]);

    const fetchEvents = async () => {
        const { data } = await supabase.from('events').select('id, title').order('date', { ascending: false });
        setEvents(data || []);
    };

    const fetchAlbum = async () => {
        const { data, error } = await supabase.from('albums').select('*').eq('id', id).single();
        if (error) {
            console.error('Error fetching album:', error);
            navigate('/admin/gallery');
        } else {
            setAlbum(data);
        }
    };

    const fetchPhotos = async () => {
        const { data } = await supabase.from('photos').select('*').eq('album_id', id).order('created_at', { ascending: false });
        setPhotos(data || []);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const missing = [];
        if (!album.title?.trim()) missing.push('le titre');
        if (!album.date) missing.push('la date');
        if (missing.length) {
            alert(`Merci de renseigner ${missing.join(' et ')}.`);
            return;
        }

        setLoading(true);

        try {
            let albumId = id;
            const albumData = {
                title: album.title,
                description: album.description,
                date: album.date,
                event_id: album.event_id || null,
                updated_at: new Date()
            };

            if (isEditing) {
                const { error } = await supabase.from('albums').update(albumData).eq('id', id);
                if (error) throw error;
            } else {
                console.log('Creating album with data:', albumData);
                const { data, error } = await supabase.from('albums').insert([albumData]).select().single();

                if (error) {
                    console.error('Supabase error:', error);
                    throw error;
                }

                if (!data) {
                    console.error('No data returned from insert');
                    throw new Error('Erreur: Aucune donnée retournée lors de la création.');
                }

                console.log('Album created:', data);
                albumId = data.id;
            }

            // If it was a new album, redirect to edit mode to allow photo uploads
            if (!isEditing) {
                navigate(`/admin/gallery/${albumId}`);
            } else {
                alert('Album mis à jour avec succès');
            }
        } catch (error) {
            console.error('Error saving album:', error);
            alert('Erreur lors de la sauvegarde de l\'album');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e) => {
        if (!isEditing) {
            alert('Veuillez d\'abord créer l\'album avant d\'ajouter des photos.');
            return;
        }

        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setUploading(true);
        let uploadedCount = 0;

        try {
            for (const file of files) {
                const fileExt = file.name.split('.').pop();
                const fileName = `${Math.random()}.${fileExt}`;
                const filePath = `albums/${id}/${fileName}`;

                const { error: uploadError } = await supabase.storage
                    .from('gallery')
                    .upload(filePath, file);

                if (uploadError) throw uploadError;

                const { data: { publicUrl } } = supabase.storage
                    .from('gallery')
                    .getPublicUrl(filePath);

                await supabase.from('photos').insert([{
                    album_id: id,
                    url: publicUrl,
                    caption: file.name
                }]);

                // Set as cover if it's the first photo
                if (photos.length === 0 && uploadedCount === 0) {
                    await supabase.from('albums').update({ cover_image_url: publicUrl }).eq('id', id);
                    setAlbum(prev => ({ ...prev, cover_image_url: publicUrl }));
                }

                uploadedCount++;
            }
            fetchPhotos();
        } catch (error) {
            console.error('Error uploading photos:', error);
            alert('Erreur lors du téléchargement des photos');
        } finally {
            setUploading(false);
            e.target.value = null; // Reset input
        }
    };

    const handleDeletePhoto = async (photoId, photoUrl) => {
        if (!window.confirm('Supprimer cette photo ?')) return;

        try {
            // Delete record
            const { error } = await supabase.from('photos').delete().eq('id', photoId);
            if (error) throw error;

            // Optimistically update UI
            setPhotos(photos.filter(p => p.id !== photoId));

            // Note: In a real prod app, we should also delete the file from storage
            // But getting the path from the URL can be tricky depending on the setup
        } catch (error) {
            console.error('Error deleting photo:', error);
        }
    };

    const handleSetCover = async (photoUrl) => {
        try {
            await supabase.from('albums').update({ cover_image_url: photoUrl }).eq('id', id);
            setAlbum(prev => ({ ...prev, cover_image_url: photoUrl }));
        } catch (error) {
            console.error('Error updating cover:', error);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={() => navigate('/admin/gallery')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                    <ArrowLeft size={24} className="text-gray-500" />
                </button>
                <h1 className="text-2xl font-serif font-bold text-gray-900">
                    {isEditing ? 'Modifier l\'album' : 'Nouvel Album'}
                </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Album Details Form */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                                <input
                                    type="text"
                                    value={album.title}
                                    onChange={(e) => setAlbum({ ...album, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                <input
                                    type="date"
                                    value={album.date}
                                    onChange={(e) => setAlbum({ ...album, date: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Événement lié (optionnel)</label>
                                <select
                                    value={album.event_id || ''}
                                    onChange={(e) => setAlbum({ ...album, event_id: e.target.value || null })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                >
                                    <option value="">Aucun</option>
                                    {events.map(event => (
                                        <option key={event.id} value={event.id}>{event.title}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    rows={4}
                                    value={album.description || ''}
                                    onChange={(e) => setAlbum({ ...album, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                <Save size={18} />
                                {loading ? 'Sauvegarde...' : 'Enregistrer les détails'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Photos Management */}
                <div className="lg:col-span-2 space-y-6">
                    {!isEditing ? (
                        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-12 text-center text-gray-500">
                            <p>Enregistrez d'abord l'album pour ajouter des photos.</p>
                        </div>
                    ) : (
                        <>
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                                <h3 className="font-bold text-gray-900 mb-4">Ajouter des photos</h3>
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:bg-gray-50 transition-colors relative">
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleFileUpload}
                                        disabled={uploading}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    />
                                    <div className="pointer-events-none">
                                        <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                                        <p className="text-gray-600 font-medium">
                                            {uploading ? 'Téléchargement en cours...' : 'Cliquez ou glissez des photos ici'}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP supportés</p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                <AnimatePresence>
                                    {photos.map((photo) => (
                                        <motion.div
                                            key={photo.id}
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                            className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden"
                                        >
                                            <img
                                                src={photo.url}
                                                alt={photo.caption}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => handleSetCover(photo.url)}
                                                    className="p-1.5 bg-white text-primary-900 rounded-full hover:bg-gray-100"
                                                    title="Définir comme couverture"
                                                >
                                                    <Image size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePhoto(photo.id, photo.url)}
                                                    className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                                                    title="Supprimer"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                            {album.cover_image_url === photo.url && (
                                                <div className="absolute top-2 left-2 bg-accent-500 text-white text-xs px-2 py-0.5 rounded-full shadow-sm">
                                                    Couverture
                                                </div>
                                            )}
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
